import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Mail, Bot, FileText, RefreshCw } from 'lucide-react';
import BSDCLIProject from './2025/BSDCLIProject';
import EmailAssistantProject from './2025/EmailAssistantProject';
import TransformationRobotProject from './2025/TransformationRobotProject';
import RFPAssistantProject from './2025/RFPAssistantProject';
import TechRefreshProject from './2025/TechRefreshProject';

const InnovationChallenge2025 = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('tech-refresh');

  const tabs = [
    {
      id: 'tech-refresh',
      name: 'Tech Refresh',
      icon: RefreshCw,
      color: 'from-yellow-500 to-amber-500',
      borderColor: 'border-yellow-500'
    },
    {
      id: 'rfp-assistant',
      name: 'RFP Assistant',
      icon: FileText,
      color: 'from-orange-500 to-red-500',
      borderColor: 'border-orange-500'
    },
    {
      id: 'transformation-robot',
      name: 'TPR',
      icon: Bot,
      color: 'from-purple-500 to-pink-500',
      borderColor: 'border-purple-500'
    },
    {
      id: 'bsd-cli',
      name: 'BSD CLI',
      icon: Brain,
      color: 'from-blue-500 to-cyan-500',
      borderColor: 'border-blue-500'
    },
    {
      id: 'email-assistant',
      name: 'Email Assistant',
      icon: Mail,
      color: 'from-green-500 to-teal-500',
      borderColor: 'border-green-500'
    }
  ];

  const renderActiveContent = () => {
    switch (activeTab) {
      case 'bsd-cli':
        return <BSDCLIProject />;
      case 'email-assistant':
        return <EmailAssistantProject />;
      case 'transformation-robot':
        return <TransformationRobotProject />;
      case 'rfp-assistant':
        return <RFPAssistantProject />;
      case 'tech-refresh':
        return <TechRefreshProject />;
      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="modal-content max-w-6xl max-h-[90vh] overflow-y-auto pr-2 custom-scrollbar"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-btn" onClick={onClose}>×</button>

        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold mb-2">🚀 2025 Innovation Challenge</h2>
          <p className="text-gray-300">Five Major AI Projects</p>
        </div>

        {/* Folder-style Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 justify-center bg-gray-900/50 rounded-t-xl p-2 border-b border-white/10">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <motion.button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative px-6 py-3 rounded-lg font-semibold text-sm transition-all ${
                    isActive
                      ? `${tab.borderColor} border-2 bg-white/10 shadow-lg`
                      : 'border-2 border-transparent hover:bg-white/5 text-gray-400'
                  }`}
                  whileHover={{ scale: isActive ? 1 : 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className={`absolute inset-0 bg-gradient-to-r ${tab.color} opacity-20 rounded-lg`}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <div className="relative flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                    <span className={isActive ? 'text-white' : ''}>{tab.name}</span>
                  </div>

                  {/* Active indicator */}
                  {isActive && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-white rounded-full"
                    />
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Content Area */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="min-h-[500px]"
          >
            {renderActiveContent()}
          </motion.div>
        </AnimatePresence>

      </motion.div>
    </motion.div>
  );
};

export default InnovationChallenge2025;
